'use client';

import type { ComponentPropsWithoutRef, FC } from 'react';
import NextImage, { getImageProps } from 'next/image';
import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import { twMerge } from '@tuic/tailwind-config';
import { ResponsiveValue } from '../../../utils/responsive-property';
import { screens } from '../../../utils/screen';
import { getImageLoader, changeImageFormatOrSource, changeImageQuality, changeResizingBehavior } from './contentful-helper';
import { applyContentfulCdnProxyIfEnabled } from '../../../utils/contentful';
type NextImageProps = Omit<ComponentPropsWithoutRef<typeof NextImage>, 'width' | 'height' | 'fill' | 'sizes'>;
export type ImageSizes = ResponsiveValue<string | number>;
export type ImageProps = {
  className?: string;
  sizes?: ImageSizes;
  title?: string;
  description?: string;
} & NextImageProps;
export const ImageDefault: FC<ImageProps> = ({
  src,
  alt,
  style,
  className,
  sizes,
  title,
  description,
  ...rest
}) => {
  if (!src) {
    return null;
  }
  style = {
    objectFit: 'cover',
    ...style
  };
  const derivedClassName = twMerge('aspect-1/1 bg-marine-10', 'relative overflow-hidden', className);
  return <div className={derivedClassName} data-sentry-component="ImageDefault" data-sentry-source-file="ImageDefault.tsx">
      <picture data-sentry-element="picture" data-sentry-source-file="ImageDefault.tsx">
        {renderAvifSourceElement(src, alt, sizes)}
        <NextImage {...rest} src={applyContentfulCdnProxyIfEnabled(src)} alt={alt} title={title} data-description={description} loader={getImageLoader(src)} sizes={parseSizes(sizes)} fill style={style} className="isolate" data-sentry-element="NextImage" data-sentry-source-file="ImageDefault.tsx" />
      </picture>
    </div>;
};

/**
 * Parse the image sizes from the responsive property to a valid `sizes` attribute value.
 */
const parseSizes = (sizes?: ResponsiveValue<string | number>): undefined | string => {
  if (typeof sizes === 'undefined') {
    return undefined;
  }
  if (!Array.isArray(sizes)) {
    return sizeToCssSize(sizes);
  }
  const [def, indexed] = sizes;
  if (typeof indexed === 'undefined') {
    return sizeToCssSize(def);
  }
  return [createSizeMediaQuery(screens.xxl, indexed.xxl), createSizeMediaQuery(screens.xl, indexed.xl), createSizeMediaQuery(screens.lg, indexed.lg), createSizeMediaQuery(screens.md, indexed.md), createSizeMediaQuery(screens.sm, indexed.sm), createSizeMediaQuery(screens.xs, indexed.xs), sizeToCssSize(def)].filter(item => !!item).join(', ');
};

/**
 * Transform the given size to a valid CSS size value.
 */
const sizeToCssSize = (size: number | string) => {
  if (typeof size === 'number') {
    return `${size}vw`;
  }
  if (size.includes('px')) {
    return size;
  }
  const asNumber = parseInt(size, 10);
  if (Number.isNaN(asNumber)) {
    return size;
  }
  return `${asNumber}vw`;
};

/**
 * Create a media query for the given screen width and value.
 */
const createSizeMediaQuery = (screenWidth: number, value?: number | string) => {
  if (typeof value === 'undefined') {
    return null;
  }
  return `(min-width: ${screenWidth}px) ${sizeToCssSize(value)}`;
};
const transformSrcSetToAvif = (srcSet: string | undefined) => {
  if (!srcSet) {
    return undefined;
  }
  return srcSet.split(', ').map((srcSetEntry: string) => {
    // only change the URI part of each srcSet line
    const [uri, sizeParam] = srcSetEntry.split(' ');
    // avif retains quality very well at high compression ratios
    let src = changeImageQuality(uri, 60);
    // allow resizing the image for SVGs because they can be scaled without quality loss
    if (/\.svg(\?|$)/i.test(uri)) src = changeResizingBehavior(src, 'fill');
    const avifSrc = changeImageFormatOrSource(src, 'avif');
    return `${applyContentfulCdnProxyIfEnabled(avifSrc)} ${sizeParam}`;
  }).join(', ');
};
const renderAvifSourceElement = (src: string | StaticImport, alt: string, sizes?: ResponsiveValue<string | number>) => {
  // getImageProps is the same function that generates the imageProps (including the srcSet) for NextImage
  // we want to generate the same srcSet and sizes attribute as the NextImage has but with avif as image type
  const imageProps = getImageProps({
    alt,
    src,
    loader: getImageLoader(src),
    sizes: parseSizes(sizes),
    fill: true
  });
  const srcSetAvif = transformSrcSetToAvif(imageProps.props.srcSet);
  return <source type="image/avif" sizes={imageProps.props.sizes} srcSet={srcSetAvif} data-sentry-component="renderAvifSourceElement" data-sentry-source-file="ImageDefault.tsx" />;
};